import React, { useCallback, useEffect, useRef, useState } from 'react';
import { rowFor } from '../utils/rtl';
import { ActivityIndicator, Image, Linking, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { apiService } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import type { WorkspaceAttachment } from '../services/workspace-assistant.types';
import { colors, typography } from '../theme';

export const MAX_ATTACHMENTS = 4;
const MAX_BYTES = 10 * 1024 * 1024;
/** What the assistant reads: photos, PDF, Word, Excel, CSV and text. */
const TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'text/plain', 'application/json', 'text/markdown'];
const EXTENSIONS = /\.(jpe?g|png|webp|gif|pdf|docx|xlsx|csv|txt|json|md)$/i;

type Pending = { key: string; name: string; type: string; size: number; preview?: string; status: 'uploading' | 'ready' | 'error'; error?: string; attachment?: WorkspaceAttachment };
type Picked = { file?: File; uri?: string; name: string; type: string; size: number };

const isImage = (type: string, name: string) => type.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(name);
const fileIcon = (type: string, name: string): keyof typeof Ionicons.glyphMap => isImage(type, name) ? 'image-outline'
  : /pdf/.test(type) || /\.pdf$/i.test(name) ? 'document-text-outline' : /sheet|csv/.test(type) || /\.(xlsx|csv)$/i.test(name) ? 'grid-outline' : 'document-outline';
const sizeText = (bytes: number) => bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Files waiting to go with the next message: each uploads (and is read on the server) as soon as it's added. */
export function useWorkspaceAttachments() {
  const { t } = useLocale();
  const [items, setItems] = useState<Pending[]>([]);
  const previews = useRef<string[]>([]);
  useEffect(() => () => { if (Platform.OS === 'web') previews.current.forEach(u => URL.revokeObjectURL(u)); }, []);

  const count = useRef(0);
  count.current = items.length;
  const add = useCallback((picked: Picked[]) => {
    const room = MAX_ATTACHMENTS - count.current;
    const next: Pending[] = [];
    for (const p of picked.slice(0, Math.max(0, room))) {
      const key = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const supported = TYPES.includes(p.type) || EXTENSIONS.test(p.name);
      const error = !supported ? t('workspaceAssistant.attach.unsupported') : p.size > MAX_BYTES ? t('workspaceAssistant.attach.tooLarge') : undefined;
      let preview: string | undefined;
      if (!error && isImage(p.type, p.name)) {
        preview = p.file && Platform.OS === 'web' ? URL.createObjectURL(p.file) : p.uri;
        if (p.file && preview) previews.current.push(preview);
      }
      next.push({ key, name: p.name, type: p.type, size: p.size, preview, status: error ? 'error' : 'uploading', error });
      if (!error) void apiService.uploadWorkspaceAttachment(p.file || { uri: p.uri!, type: p.type, name: p.name }).then(result => {
        const message = (result.errors as any[])?.map(e => typeof e === 'string' ? e : e?.message).filter(Boolean).join(' ');
        setItems(list => list.map(i => i.key !== key ? i : result.statusCode === 1000 && result.data
          ? { ...i, status: 'ready', attachment: result.data } : { ...i, status: 'error', error: message || t('workspaceAssistant.attach.failed') }));
      });
    }
    count.current += next.length;
    setItems(current => [...current, ...next]);
  }, [t]);

  const pick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: TYPES, multiple: true, copyToCacheDirectory: true });
      if (result.canceled) return;
      add(result.assets.map(a => ({ file: (a as any).file, uri: a.uri, name: a.name || 'file', type: a.mimeType || (a as any).file?.type || '', size: a.size ?? (a as any).file?.size ?? 0 })));
    } catch { /* picker closed or unavailable */ }
  }, [add]);

  /** Web: files dropped on the panel or pasted into the box (screenshots). */
  const addFiles = useCallback((files: FileList | File[] | null | undefined) => {
    const list = Array.from(files || []);
    if (list.length) add(list.map(f => ({ file: f, name: f.name || `pasted-${Date.now()}.png`, type: f.type, size: f.size })));
    return list.length > 0;
  }, [add]);

  const remove = useCallback((key: string) => setItems(list => list.filter(i => i.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);
  const uploading = items.some(i => i.status === 'uploading');
  const ready = items.filter(i => i.status === 'ready' && i.attachment).map(i => i.attachment!);
  return { items, pick, addFiles, remove, clear, uploading, ready, full: items.length >= MAX_ATTACHMENTS };
}

/** The files above the message box, each removable before sending. */
export function AttachmentTray({ items, onRemove, isRTL }: { items: Pending[]; onRemove: (key: string) => void; isRTL: boolean }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  if (!items.length) return null;
  return <View style={[styles.tray, { flexDirection: rowFor(isRTL) }]}>
    {items.map(item => <View key={item.key} style={[styles.chip, { flexDirection: rowFor(isRTL) }, item.status === 'error' && styles.chipError]}>
      {item.preview ? <Image source={{ uri: item.preview }} style={styles.thumb} /> : <View style={styles.iconBox}><Ionicons name={fileIcon(item.type, item.name)} size={18} color={colors.primary as string} /></View>}
      <View style={styles.chipText}>
        <Text style={styles.name} numberOfLines={1}>{item.name}</Text>
        <Text style={[styles.sub, item.status === 'error' && { color: colors.status.error as string }]} numberOfLines={1}>
          {item.status === 'error' ? item.error : item.status === 'uploading' ? t('workspaceAssistant.attach.reading') : item.attachment?.note || sizeText(item.size)}
        </Text>
      </View>
      {item.status === 'uploading' ? <ActivityIndicator size="small" color={colors.primary as string} />
        : <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.attach.remove')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={() => onRemove(item.key)}>
          <Ionicons name="close-circle" size={18} color={colors.text.secondary as string} />
        </TouchableOpacity>}
    </View>)}
  </View>;
}

/** A sent message's files: photo thumbnails and file chips that open the original. */
export function MessageAttachments({ attachments, isRTL }: { attachments: WorkspaceAttachment[]; isRTL: boolean }) {
  const styles = useThemedStyles(getStyles);
  const open = (url?: string) => { if (!url) return; if (Platform.OS === 'web') window.open(url, '_blank', 'noopener'); else void Linking.openURL(url); };
  return <View style={[styles.sent, { flexDirection: rowFor(isRTL) }]}>
    {attachments.map(a => a.kind === 'image' && a.url
      ? <TouchableOpacity key={a.id} accessibilityRole="imagebutton" accessibilityLabel={a.name} onPress={() => open(a.url)}><Image source={{ uri: a.url }} style={styles.sentImage} /></TouchableOpacity>
      : <TouchableOpacity key={a.id} accessibilityRole="button" disabled={!a.url} onPress={() => open(a.url)} style={[styles.sentFile, { flexDirection: rowFor(isRTL) }]}>
        <Ionicons name={fileIcon(a.type, a.name)} size={16} color={colors.primary as string} />
        <Text style={styles.sentName} numberOfLines={1}>{a.name}</Text>
      </TouchableOpacity>)}
  </View>;
}

const getStyles = () => StyleSheet.create({
  tray: { flexWrap: 'wrap', gap: 6, paddingBottom: 6 },
  chip: { alignItems: 'center', gap: 8, paddingVertical: 5, paddingStart: 5, paddingEnd: 8, borderRadius: 10, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.secondary, maxWidth: 230 },
  chipError: { borderColor: colors.status.error },
  thumb: { width: 34, height: 34, borderRadius: 6, backgroundColor: colors.background.primary },
  iconBox: { width: 34, height: 34, borderRadius: 6, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  chipText: { flexShrink: 1, minWidth: 0 },
  name: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  sub: { fontSize: 11, color: colors.text.secondary },
  sent: { flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', marginBottom: 4 },
  sentImage: { width: 120, height: 120, borderRadius: 10, backgroundColor: colors.background.secondary },
  sentFile: { alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10, backgroundColor: colors.background.secondary, maxWidth: 220 },
  sentName: { fontSize: 12, color: colors.text.primary, flexShrink: 1 },
});
